import { InputBindingCell } from "./InputBindingCell";
import type {
  BindingTarget,
  BoundDocumentId,
  FeedsReadState,
} from "../../lib/analyticsFeeds";

/**
 * A document row's `WORKFLOW PARAMETERS` cell: which of the test table's
 * `(column, param_key)` pairs this document fills.
 *
 * The subject is the row's own document and can be nothing else, so the cell
 * cannot express a binding to a file the row is not for. What the control looks
 * like, and what it offers, is `InputBindingCell`'s — shared with the generated
 * sample row, which asks the same question of the same list.
 *
 * The analytics-input flag is the gate, and it lives in its own column beside
 * this one. The two answer different questions — the flag says *this document is
 * an analytics input*, the binding says *this parameter is filled by that
 * document* — and a cell holding both shows neither gating the other, so they
 * are adjacent rather than combined.
 */
interface AnalyticsParametersCellProps {
  documentId: BoundDocumentId;
  documentName: string;
  /**
   * The row's flag, or `undefined` where the read it comes from carried none.
   * The two are not interchangeable here: the flag is this cell's gate, and only
   * a `false` the server actually sent may close it.
   */
  isAnalyticsInput: boolean | undefined;
  targets: readonly BindingTarget[];
  documentNames: ReadonlyMap<BoundDocumentId, string>;
  feedsState: FeedsReadState;
  deletedColumnNames: readonly string[];
  pending: boolean;
  writesHeldReason: string | null;
  onAssign: (documentId: BoundDocumentId, target: BindingTarget) => void;
  onClear: (target: BindingTarget) => void;
}

export function AnalyticsParametersCell({
  documentId,
  documentName,
  isAnalyticsInput,
  targets,
  documentNames,
  feedsState,
  deletedColumnNames,
  pending,
  writesHeldReason,
  onAssign,
  onClear,
}: AnalyticsParametersCellProps) {
  // An unflagged document is not an analytics input, so there is no parameter
  // for it to fill and no editor to open. The cell says so in a control of its
  // own rather than standing empty: an empty cell reads as *this column does not
  // apply to this row*, when what is true is that this row could fill a
  // parameter the moment it is flagged, and the flag is one column away.
  //
  // Only an explicit `false` closes the gate. An absent flag is not an answer,
  // and the inert control is not a neutral rendering of one: it withholds the
  // bindings this file already fills, so a read that dropped the flag would take
  // away the only surface those bindings can be seen or cleared from. On an
  // unknown flag the cell reports what it holds instead, which is what the
  // editor inside it does with every other value it cannot vouch for.
  const fillsNothing = isAnalyticsInput === false;

  return (
    <InputBindingCell
      subject={{
        kind: "document",
        documentId,
        onFill: (target) => onAssign(documentId, target),
      }}
      subjectName={documentName}
      targets={targets}
      documentNames={documentNames}
      feedsState={feedsState}
      deletedColumnNames={deletedColumnNames}
      pending={pending}
      writesHeldReason={writesHeldReason}
      onClear={onClear}
      disabled={fillsNothing}
    />
  );
}
